import { memo, useEffect, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiContentDrawer,
  MiDetailFields,
  MiDrawerHeader,
  MiIcon,
  MiListGroupedCard,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import {
  ACTIONS_LIST_TYPE,
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  BUTTON_COLORS,
  DATE_TIME_FORMATS,
  MIBUILD_PAGE_HEADER_HEIGHT,
  MODAL_TITLES,
  ORDER_STATUS_BORDER_COLORS,
  ORDER_STATUS_COLORS,
  ORDER_STATUS_TITLE_COLORS,
  SECTION_HEADER_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import {
  getWorkOrderTabStats,
  groupActivitiesByDate,
  groupActivitiesForTimeline,
  separateWords,
  toFormattedDate,
} from "@miplatform/utils";
import {
  mdiCancel,
  mdiClipboardTextOutline,
  mdiClose,
  mdiContentSave,
  mdiHomeOutline,
  mdiInformationOutline,
  mdiPencil,
  mdiPrinterOutline,
} from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { CloseoutContacts } from "../../views/Schedule/Scheduler/CloseoutDrawer/Tabs/CloseoutContacts/CloseoutContacts";
import { ActivityCard } from "../ActivityCard/ActivityCard";
import { ActivityTimeline } from "../ActivityTimeline/ActivityTimeline";
import { Footer } from "../Footer/Footer";
import { Info } from "../Info/Info";
import { useOrderActivityDrawerViewModel } from "./useOrderActivityDrawerViewModel";

const OrderDetailsTab = ({ order, classes, onRefresh, updateOrder, theme }) => {
  const modifiedOrder = {
    ...order,
    orderTemplateTradeName: order.orderTemplateTradeName ?? "--",
    home: {
      ...order.home,
      communityName: order.home?.communityName ?? "--",
    },
    inProgressStartDate: order.inProgressStartDate
      ? moment(order.inProgressStartDate).format(
          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
        )
      : "--",
    approvedStartDate: order.approvedStartDate
      ? moment(order.approvedStartDate).format(
          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
        )
      : "--",
    notes: !order.notes ? "--" : order.notes,
    builderNotes: !order.builderNotes ? "--" : order.builderNotes,
  };
  const edit = useEdit(modifiedOrder);
  const orderDetailsFieldset = [
    {
      label: "Trade",
      name: "orderTemplateTradeName",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Start Date",
      name: "inProgressStartDate",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Scheduled Date",
      name: "approvedStartDate",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Community",
      name: "home.communityName",
      width: "25%",
      readOnly: true,
    },
  ];

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [editNotesFieldset, setEditNotesFieldset] = useState(false);

  const notesFieldset = [
    {
      label: "Order Notes",
      name: "notes",
      width: "50%",
      readOnly: !editNotesFieldset,
      multiline: true,
      rows: 4,
    },
  ];

  return (
    <Box
      className={classes.tabContent}
      height="100%"
      display="flex"
      flexDirection="column"
      flex={1}
    >
      <MiDetailFields
        headerProps={{
          title: "Order Details",
          icon: mdiInformationOutline,
          variant: SECTION_HEADER_VARIANTS.UNDERLINED,
        }}
        detailFields={orderDetailsFieldset}
        edit={edit}
      />

      <Box flex={1}>
        <MiDetailFields
          headerProps={{
            title: "Notes",
            icon: mdiClipboardTextOutline,
            variant: SECTION_HEADER_VARIANTS.UNDERLINED,
            rightIcon: mdiPencil,
            onRightIconClick: () => setEditNotesFieldset(true),
          }}
          detailFields={notesFieldset}
          edit={edit}
        />
      </Box>
      <Footer
        sticky
        isDirty={edit.isDirty()}
        show={editNotesFieldset}
        handleSaveClick={() => setOpenConfirmModal(true)}
        cancelTitleIcon={{ path: mdiCancel, color: theme.palette.red.main }}
        handleCancelClick={() => {
          edit.reset(modifiedOrder);
          setEditNotesFieldset(false);
        }}
        disableSave={!edit.isDirty()}
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        titleIcon={{
          path: mdiContentSave,
        }}
        textPositive="Yes, Save"
        open={openConfirmModal}
        handleNegative={() => setOpenConfirmModal(false)}
        handlePositive={() => {
          updateOrder({
            orderId: order.id,
            data: edit.edits,
            orderModifiedOn: order.modifiedOn,
            onRefresh,
          });
          edit.reset({ ...modifiedOrder, ...edit.edits });
          setOpenConfirmModal(false);
          setEditNotesFieldset(false);
        }}
        description="You are about to save changes to the order details. Are you sure you would like to continue?"
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </Box>
  );
};

const WorkOrderCard = ({ workOrder, classes, theme }) => {
  const { status = "--", templateName = "--", tradeName = "--" } = workOrder;

  return (
    <Box
      className={classes.workOrderCard}
      style={{ border: `1px solid ${ORDER_STATUS_BORDER_COLORS[status]}` }}
    >
      <Box display="flex" alignItems="center" gap={4}>
        <Box>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.CAPTION}
            color={theme.palette.grey.medium}
            display="block"
          >
            Status
          </Typography>
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            color={
              ORDER_STATUS_TITLE_COLORS[status] || theme.palette.common.black
            }
          >
            {separateWords(status)}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
        <Box>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.primary.main}
          >
            {templateName}
          </Typography>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.CAPTION}
            color={theme.palette.grey.medium}
          >
            {tradeName}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const WorkOrdersTab = ({ workOrders = [], classes, theme }) => {
  return (
    <Box className={classes.tabContent}>
      {workOrders.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Typography color={theme.palette.grey.medium}>
            No work orders available
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {workOrders.map((workOrder) => (
            <WorkOrderCard
              key={workOrder.id}
              workOrder={workOrder}
              classes={classes}
              theme={theme}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const SubHeader = ({ order }) => {
  const {
    orderStatus,
    inProgressStartedDate,
    approvedStartDate,
    requester,
    completeDate,
  } = order;

  const { classes } = useStyles({
    statusColor: ORDER_STATUS_COLORS[orderStatus],
    statusBorderColor: ORDER_STATUS_BORDER_COLORS[orderStatus],
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.statusContainer}>
        <Info
          text="Order Status"
          subtext={separateWords(orderStatus)}
          descriptionColor={ORDER_STATUS_TITLE_COLORS[orderStatus]}
        />
      </Box>
      <Box className={classes.infoContainer}>
        <Info
          text="Scheduled Date"
          subtext={toFormattedDate(
            approvedStartDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
          )}
        />
        <Info
          text="Start Date"
          subtext={toFormattedDate(
            inProgressStartedDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
          )}
        />
        <Info
          text="Complete Date"
          subtext={toFormattedDate(
            completeDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
          )}
        />
        <Info
          text="Construction Manager"
          subtext={
            requester?.displayName ||
            (requester?.firstName && requester?.lastName
              ? `${requester.firstName} ${requester.lastName}`
              : "--")
          }
        />
        <Info text="Order #" subtext={order.orderNumber} />
      </Box>
    </Box>
  );
};

export const OrderActivityDrawer = memo(
  ({
    open = false,
    onClose = () => null,
    order = {},
    getOrderActivity,
    onRefresh = () => null,
  }) => {
    const { classes, theme } = useStyles({});
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const {
      orderActivities,
      currentTab,
      setCurrentTab,
      currentHome,
      handlePrint,
      handleClose,
      fetchTenantAccount,
      fetchActivities,
      fetchHomeDetails,
      reset,
      updateOrder,
    } = useOrderActivityDrawerViewModel({
      open,
      order,
      getOrderActivity,
      onClose,
    });

    useEffect(() => {
      if (open) {
        reset();
        Promise.all([
          fetchTenantAccount(),
          fetchActivities(),
          fetchHomeDetails(),
        ]);
      }
    }, [open, order?.id]);

    const { workOrders = [] } = order;
    const { address = { city: "", regionCode: "" } } = currentHome;
    const { icon, color, label } = getWorkOrderTabStats(theme, workOrders);

    const tabs = [
      {
        label: "Order Details",
        value: "orderDetails",
      },
      {
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <MiIcon
              path={icon}
              size={0.7}
              color={color}
              style={{
                marginBottom: theme.spacing(1),
              }}
            />
            {label}
          </Box>
        ),
        value: "workOrders",
      },
      {
        label: "Activity",
        value: "activity",
      },
      {
        label: "Contacts",
        value: "contacts",
      },
    ];

    const actions = [
      {
        name: "Print",
        icon: mdiPrinterOutline,
        onClick: handlePrint,
      },
      {
        name: "Close",
        icon: mdiClose,
        onClick: handleClose,
      },
    ];

    const header = (
      <MiDrawerHeader
        title={address.addressLine1}
        subtitle={[
          address && `${address.city}, ${address.regionCode}`,
          currentHome.communityName,
          currentHome.builderName,
        ]}
        leftIcon={mdiHomeOutline}
        rightAction={
          <MiActions
            type={ACTIONS_LIST_TYPE.ICONS}
            actions={actions}
            buttonsProps={{ sx: { padding: 1 } }}
          />
        }
      />
    );

    const DRAWER_TOP_OFFSET =
      (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
      MIBUILD_PAGE_HEADER_HEIGHT;

    const renderTabContent = () => {
      switch (currentTab) {
        case "orderDetails":
          return (
            <OrderDetailsTab
              order={{ ...order, home: currentHome }}
              classes={classes}
              theme={theme}
              onRefresh={onRefresh}
              updateOrder={updateOrder}
            />
          );
        case "workOrders":
          return (
            <WorkOrdersTab
              workOrders={workOrders}
              classes={classes}
              theme={theme}
            />
          );
        case "activity":
          return (
            <Box className={classes.timelineContainer}>
              {Object.entries(groupActivitiesByDate(orderActivities)).map(
                ([date, activities]) => {
                  const timelineData = {
                    [date]: groupActivitiesForTimeline(activities),
                  };

                  return (
                    <MiListGroupedCard
                      key={date}
                      title={date}
                      renderCard={
                        <ActivityTimeline
                          disableSpacing
                          data={timelineData}
                          showHeader={false}
                          showDate={false}
                          renderDetail={(activity) => (
                            <ActivityCard
                              key={activity.id}
                              activity={activity}
                            />
                          )}
                        />
                      }
                    />
                  );
                }
              )}
            </Box>
          );
        case "contacts":
          return <CloseoutContacts home={currentHome} onRefresh={onRefresh} />;
        default:
          return null;
      }
    };

    return (
      <MiContentDrawer
        variant="persistent"
        open={open}
        onClose={handleClose}
        slotProps={{
          transition: {
            unmountOnExit: true,
          },
        }}
        hideBackdrop
        sx={{
          "& > .MuiPaper-root": {
            top: DRAWER_TOP_OFFSET,
            height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
          },
        }}
      >
        <Box className={classes.viewContainer} height="inherit">
          {header}
          <Box className={classes.content}>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.BODY1}
              fontWeight={theme.fontWeight.semiBold}
            >
              {order.orderTemplateName}
            </Typography>
            <SubHeader order={order} />
            <Box className={classes.tabContainer}>
              <MiTab
                isSecondary
                tabs={tabs}
                currentTab={currentTab}
                onTabChange={setCurrentTab}
              />
            </Box>
            {renderTabContent()}
          </Box>
        </Box>
      </MiContentDrawer>
    );
  }
);

const useStyles = makeStyles()((theme, { statusColor, statusBorderColor }) => ({
  viewContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
  },
  content: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
    height: "100%",
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.grey.medium}`,
    paddingVertical: theme.spacing(2),
  },
  root: {
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "center",
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${statusBorderColor}`,
    backgroundColor: `${statusColor}1A`,
  },
  statusContainer: {
    padding: theme.spacing(2, 7, 2, 4),
    height: "100%",
    alignContent: "center",
    backgroundColor: `${statusColor}2A`,
    borderRight: `1px solid ${statusBorderColor}`,
  },
  infoContainer: {
    display: "flex",
    gap: theme.spacing(8),
    padding: theme.spacing(1, 3),
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
    "& > *": {
      flex: "1 1 0",
      minWidth: 0,
    },
  },
  timelineContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
  workOrderCard: {
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey.accent}`,
  },
}));
